import { Timestamp } from 'firebase/firestore';

// ============================================
// User Types & Enums
// ============================================

export type UserRole = 'viewer' | 'editor' | 'manager' | 'admin' | 'superadmin';
export type UserStatus = 'pending' | 'active' | 'suspended' | 'deactivated';
export type AuthProvider = 'email' | 'google' | 'sso';
export type Country = 'IL' | 'UK' | 'US';

// ============================================
// Main User Interface
// ============================================

export interface SpotLockUser {
  // Identity
  id: string;
  email: string;
  name: string;
  phone?: string;
  avatarUrl?: string;

  // Account
  accountId: string;

  // Role & Status
  role: UserRole;
  status: UserStatus;

  // Auth
  authProvider: AuthProvider;
  ssoProviderId?: string;

  // Preferences
  country: Country;
  timezone?: string;
  locale?: string;

  // Timestamps
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  lastLoginAt?: Timestamp;
  loginCount?: number;

  // Audit
  createdBy?: string;
  invitedBy?: string;

  // Legacy fields (deprecated - remove after migration verified)
  /** @deprecated Use role instead */
  isAdmin?: boolean;
  /** @deprecated Use status instead */
  isApproved?: boolean;
  /** @deprecated Use role/permissions instead */
  isEditEnabled?: boolean;
  /** @deprecated Remove */
  isDemo?: boolean;
  /** @deprecated Remove */
  isDebug?: boolean;

  // Feature flags
  menuItem?: { name: string; link: string }[];
}

// ============================================
// Helper Functions
// ============================================

/** Check if user has admin or superadmin role */
export const isAdminUser = (user: SpotLockUser | null | undefined): boolean => {
  if (!user) return false;
  // Support both new role field and legacy isAdmin
  return user.role === 'admin' || user.role === 'superadmin' || user.isAdmin === true;
};

/** Check if user has superadmin role (global access) */
export const isSuperAdmin = (user: SpotLockUser | null | undefined): boolean => {
  if (!user) return false;
  return user.role === 'superadmin';
};

/** Check if user is active */
export const isActiveUser = (user: SpotLockUser | null | undefined): boolean => {
  if (!user) return false;
  // Support both new status field and legacy isApproved
  return user.status === 'active' || user.isApproved === true;
};

/** Check if user can edit data */
export const canEditData = (user: SpotLockUser | null | undefined): boolean => {
  if (!user) return false;
  const editRoles: UserRole[] = ['editor', 'manager', 'admin', 'superadmin'];
  return editRoles.includes(user.role) || user.isEditEnabled === true;
};

/** Check if user can access a specific account */
export const canAccessAccount = (user: SpotLockUser | null | undefined, accountId: string): boolean => {
  if (!user) return false;
  return user.role === 'superadmin' || user.accountId === accountId;
};
